import { ROUTE_CONFIG } from "@/routes/config";
import { AssignStatus } from "@/enum/list.enum";

import type { CustomListsAutocompleteForSequencerItem } from "@/pages/Private/pages/List/components/CustomListsAutocompleteForSequencer";

/** The messaging surface a scope filter belongs to. Drives relevance, copy, and the cross-link. */
export enum ListScope {
	INBOX = "inbox",
	OUTBOX = "outbox",
	READY_TO_SEND = "readyToSend",
}

/**
 * The `assignStatus` values a lead must have to count towards a surface. A lead sits in exactly one
 * of these buckets at a time, which is why a list can be full of leads and still have nothing on
 * the surface the user is looking at (PREC-3834).
 *
 * **This is the single source** for that question in `apps/web`: the three request populations
 * (`OUTBOX_ASSIGN_STATUSES` in `useOutboxProspects`, `READY_TO_SEND_ASSIGN_STATUSES` in
 * `readyToSendRequestParams`, the Inbox's own `assignStatus`) and every per-lead deep link
 * (`resolveLeadLocation` → `resolveLeadReviewTarget`) read it from here. They used to be four
 * hand-written copies, and a link built against the wrong one is PREC-4103: a "Review" that opened
 * a surface whose population could never contain the lead it named.
 *
 * SQL twin: `OUTBOX_ASSIGN_STATUSES` in `apps/api/src/enums/outbox-tab.enum.ts` — the Outbox row
 * is filtered there too, and nothing but these two docblocks compares them. The Review-to-send
 * population has an API twin too: `REVIEW_ASSIGN_STATUSES` in
 * `apps/api/src/mcp/tools/review-queue.util.ts`, which the MCP review tools decide on (PREC-4372).
 */
export const LIST_SCOPE_RELEVANCE_STATUSES: Record<ListScope, AssignStatus[]> = {
	[ListScope.INBOX]: [AssignStatus.REPLIES],
	[ListScope.OUTBOX]: [
		AssignStatus.ASSIGNED,
		AssignStatus.RUNNING,
		AssignStatus.PAUSED,
		AssignStatus.MESSAGE_SENT,
		AssignStatus.ENDED,
		AssignStatus.DONE,
	],
	[ListScope.READY_TO_SEND]: [
		AssignStatus.DRAFT,
		AssignStatus.SAVED_FOR_LATER,
		AssignStatus.ARCHIVED,
	],
};

/**
 * The `listIds` query param for one list, as the Inbox, Outbox and Ready to send read it: a JSON
 * array of `{ id, title }` items, not bare ids. The title is the filter chip's label only.
 */
export const listIdsParam = (listId: number, listName?: string | null): string =>
	JSON.stringify([{ id: listId, title: listName ?? "" }]);

/**
 * Where to send a user whose list has nothing on the current surface. Inbox and Outbox are the
 * complementary pair the confusion comes from ("a lead is either in the Inbox or in the Outbox"),
 * so each points at the other. Ready to send has no unambiguous counterpart — its leads may be
 * waiting in the Outbox or already replied — so it gets the explanation without a link.
 */
const LIST_SCOPE_CROSS_LINK_ROUTE: Record<ListScope, string | null> = {
	[ListScope.INBOX]: ROUTE_CONFIG.MESSAGE_CENTER_OUTBOX,
	[ListScope.OUTBOX]: ROUTE_CONFIG.MESSAGE_CENTER_INBOX,
	[ListScope.READY_TO_SEND]: null,
};

/**
 * Link to the counterpart surface, scoped to `lists` when any are given. All three messaging pages
 * read their selection from the same `listIds` query param, so the target opens already filtered to
 * the list the user was looking for; with no lists it just opens the surface. Returns `null` for
 * scopes without a counterpart.
 */
export const buildListScopeCrossLink = (
	scope: ListScope,
	lists: CustomListsAutocompleteForSequencerItem[]
): string | null => {
	const route = LIST_SCOPE_CROSS_LINK_ROUTE[scope];

	if (!route) {
		return null;
	}

	return lists.length ? `${route}?listIds=${encodeURIComponent(JSON.stringify(lists))}` : route;
};
